import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./LandingBenchmark.module.scss";
import Leaderboard from "Explore/Pages/KeywordBenchmark/Components/Leaderboard/Leaderboard";
import BenchmarkModal from "Explore/Pages/KeywordBenchmark/Components/BenchmarkModal/BenchmarkModal";
import {
  getAllKeywords,
  getTopKeywordsAndInstitutions,
} from "Explore/Services/KeywordBenchmarking";
import { getSearchInstitutions } from "Explore/Services/Institution";
import { Skeleton } from "antd";

const HERO_CHART_IMG =
  "https://lh3.googleusercontent.com/aida/ADBb0uj2ju2XcQxDyBCxvLtXm30blK0tllYpW_scEcGeZpOKufT2OckmTiVAN-UL4pBMkRxvsiqoPjgFZmkY2rY3mqbzzaHfLuu4tjimMGdTs19Ts8MH1HI0F-R9uzONPTLqkhvuGr6_GeQKAl3PwMSL-kJpUMb3pI_ADDBpvU3KacY85IKJ_qZZ64tMYbfow38MJNxYmtEDB3A_zWnpBWSzEdKsrokSEo7sdGJb4T1_F_n5XOns3JOcLiCViKbwGIgMYXX8EIDPvvsdrtk";

const COMING_SOON_LIBRARY_IMG =
  "https://lh3.googleusercontent.com/aida-public/AB6AXuCTMtzxmHmQtZzwAxXZQM30y2WHX1jM8EKKNFxJV-JXzTuZaqm2T1BrbumMr44KLHJsVVMf-IxBapr8nsBs-rd0ZSXDh9VlUrwPAch0_h-Akk8C4cYehm0C5yVoe-Alwdb0ojw2Lm9OyM5aIpObc3J5hUNwxtcow9TcrPbwddBp1f8fSLxrK7SDfXeeAXSfBbFyDBZvzrZzwjD8Pot4nBfIemjVKRvPGSxJpiIqgkS79Caqefjf3i2WAbqq7QYtpuS4ZKpRc-R_3nJb";

const IconVerifiedUser = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
  </svg>
);

const IconAnalytics = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" />
  </svg>
);

const IconAutoAwesome = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="m19 9 1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z" />
  </svg>
);

const IconLeaderboard = () => (
  <svg className={styles["pillar__icon"]} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M16 11V3H8v6H3v12h7v-6h4v6h7V11h-5zM10 5h4v4h-4V5zM8 13H5v4h3v-4zm10 0h-3v4h3v-4z" />
  </svg>
);

const IconTrending = () => (
  <svg className={styles["pillar__icon"]} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6h-6z" />
  </svg>
);

const IconCompare = () => (
  <svg className={styles["pillar__icon"]} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M10 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h5v2h2V1h-2v2zm0 15H5l5-6v6zm9-15h-5v2h5v13l-5-6v9h5c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
  </svg>
);

const DEMO_BARS = [
  { label: "Stanford University (Topic Ownership)", pct: 92, tone: "primary" },
  { label: "MIT (Topic Ownership)", pct: 85, tone: "secondary" },
  { label: "Harvard (Topic Ownership)", pct: 78, tone: "container" },
];

const LandingBenchmark = () => {
  const [topData, setTopData] = useState({ keywords: [], institutions: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allKeywords, setAllKeywords] = useState([]);
  const [allInstitutions, setAllInstitutions] = useState([]);
  const leaderboardsRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    Promise.all([
      getTopKeywordsAndInstitutions(15),
      getAllKeywords(),
      new Promise((resolve, reject) => {
        getSearchInstitutions(
          { page: 0, rowsPerPage: 600 },
          (res) => resolve(res),
          (err) => reject(err)
        );
      }),
    ])
      .then(([top, kwData, instRes]) => {
        if (!isMounted) return;
        setTopData(top);
        setAllKeywords(kwData);
        setAllInstitutions(instRes?.results || []);
      })
      .catch(() => {})
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const scrollToLeaderboards = useCallback(() => {
    leaderboardsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <>
      <title>Benchmark Institution by Research Topics | Academic Explorer</title>
      <meta
        name="description"
        content="Discover the top institutions and research topics by publication count, and benchmark institutions across keywords."
      />
      <div className={styles["landing"]}>
        <section
          className={styles["comingSoon"]}
          aria-labelledby="coming-soon-heading"
        >
          <div className={styles["comingSoon__bg"]} aria-hidden>
            <div className={styles["comingSoon__bgBlob1"]} />
            <div className={styles["comingSoon__bgBlob2"]} />
          </div>
          <div className={styles["comingSoon__inner"]}>
            <div className={styles["comingSoon__grid"]}>
              <div className={styles["comingSoon__copy"]}>
                <div className={styles["comingSoon__headlineBlock"]}>
                  <h1 id="coming-soon-heading" className={styles["comingSoon__headline"]}>
                    Benchmarking <br />
                    Coming Soon.
                  </h1>
                  <p className={styles["comingSoon__lead"]}>
                    We are engineering the definitive tool for{" "}
                    <span className={styles["comingSoon__leadStrong"]}>research topic comparison</span> and
                    institutional authority analysis. Elevate your research impact with data-driven
                    precision.
                  </p>
                </div>
                <div className={styles["comingSoon__cohort"]}>
                  <IconVerifiedUser className={styles["comingSoon__cohortIcon"]} />
                  <p className={styles["comingSoon__cohortText"]}>
                    Exclusive cohort opening Q4 2026
                  </p>
                </div>
              </div>
              <div className={styles["comingSoon__bentoWrap"]}>
                <div className={styles["comingSoon__bento"]}>
                  <div className={styles["comingSoon__cardMetric"]}>
                    <div className={styles["comingSoon__cardMetricTop"]}>
                      <span className={styles["comingSoon__cardMetricLabel"]}>METRIC_01</span>
                      <IconAnalytics className={styles["comingSoon__cardMetricIcon"]} />
                    </div>
                    <div className={styles["comingSoon__cardMetricBars"]}>
                      <div className={styles["comingSoon__cardMetricTrack"]}>
                        <div
                          className={styles["comingSoon__cardMetricFill"]}
                          style={{ width: "75%" }}
                        />
                      </div>
                      <div className={styles["comingSoon__cardMetricTrack"]}>
                        <div
                          className={`${styles["comingSoon__cardMetricFill"]} ${styles["comingSoon__cardMetricFillDim"]}`}
                          style={{ width: "50%" }}
                        />
                      </div>
                      <p className={styles["comingSoon__cardMetricCaption"]}>Benchmark Authority</p>
                    </div>
                  </div>
                  <div className={styles["comingSoon__cardImage"]}>
                    <img
                      className={styles["comingSoon__cardImageImg"]}
                      src={COMING_SOON_LIBRARY_IMG}
                      alt=""
                      width={400}
                      height={500}
                    />
                    <div className={styles["comingSoon__cardImageCaption"]}>
                      <div className={styles["comingSoon__cardImageCaptionInner"]}>
                        <p className={styles["comingSoon__cardImageCaptionText"]}>
                          Institutional Depth
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className={styles["comingSoon__cardWide"]}>
                    <div className={styles["comingSoon__cardWideGlow"]} aria-hidden />
                    <div className={styles["comingSoon__cardWideInner"]}>
                      <div className={styles["comingSoon__cardWideRow"]}>
                        <IconAutoAwesome className={styles["comingSoon__cardWideIcon"]} />
                        <p className={styles["comingSoon__cardWideEyebrow"]}>Intelligence Layer</p>
                      </div>
                      <p className={styles["comingSoon__cardWideTitle"]}>
                        Semantic Research Topics for Global Networks.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* <section className={styles["hero"]} aria-labelledby="landing-hero-heading">
          <div className={styles["hero__grid"]}>
            <div>
              <span className={styles["hero__eyebrow"]}>The Keyword Benchmarking Authority</span>
              <h1 id="landing-hero-heading" className={styles["hero__title"]}>
                Analyze Institutional <br />
                <span className={styles["hero__titleAccent"]}>Authority &amp; Impact.</span>
              </h1>
              <p className={styles["hero__lead"]}>
                A data-driven platform to discover top institutions, identify trending research
                topics, and compare global academic authority in real-time.
              </p>
              <div className={styles["hero__actions"]}>
                <button
                  type="button"
                  className={styles["btnPrimary"]}
                  onClick={() => setIsModalOpen(true)}
                >
                  Explore Keywords
                </button>
                <button type="button" className={styles["btnSecondary"]} onClick={scrollToLeaderboards}>
                  View Leaderboard
                </button>
              </div>
            </div>
            <div className={styles["hero__visual"]}>
              <div className={styles["hero__card"]}>
                <img
                  className={styles["hero__img"]}
                  src={HERO_CHART_IMG}
                  alt="Institutional authority bar chart preview"
                  width={917}
                  height={535}
                />
              </div>
              <div className={styles["hero__glow"]} aria-hidden />
            </div>
          </div>
        </section> */}

        <section className={styles["pillars"]} aria-label="Platform capabilities">
          <div className={styles["pillars__grid"]}>
            <article className={styles["pillar"]}>
              <div className={styles["pillar__iconWrap"]}>
                <IconLeaderboard />
              </div>
              <h3 className={styles["pillar__title"]}>Discover Top Institutions</h3>
              <p className={styles["pillar__text"]}>
                Access the definitive global leaderboard. Monitor institutional authority across
                departments and research clusters with precision metrics.
              </p>
            </article>
            <article className={styles["pillar"]}>
              <div className={styles["pillar__iconWrap"]}>
                <IconTrending />
              </div>
              <h3 className={styles["pillar__title"]}>Identify Trending Research Topics</h3>
              <p className={styles["pillar__text"]}>
                Visualize topic momentum and emerging academic trends. Track the velocity of
                keywords before they redefine their respective fields.
              </p>
            </article>
            <article className={styles["pillar"]}>
              <div className={styles["pillar__iconWrap"]}>
                <IconCompare />
              </div>
              <h3 className={styles["pillar__title"]}>Compare Institutions by Topic</h3>
              <p className={styles["pillar__text"]}>
                Utilize our direct comparison tool to see how different universities stack up on
                specific research topics, subjects, and niche research areas.
              </p>
            </article>
          </div>
        </section>

        <section className={styles["comparison"]} aria-labelledby="comparison-heading">
          <div className={styles["comparison__header"]}>
            <h2 id="comparison-heading" className={styles["comparison__title"]}>
              Institutional Topic Comparison
            </h2>
            <p className={styles["comparison__subtitle"]}>
              A direct side-by-side analysis of how top-tier institutions own the narrative across
              key academic subjects.
            </p>
          </div>
          <div className={styles["comparison__panel"]}>
            <div className={styles["comparison__toolbar"]}>
              <div className={styles["comparison__topic"]}>
                <span className={styles["comparison__topicLabel"]}>Topic:</span>
                <span className={styles["comparison__topicValue"]}>Generative AI in Ethics</span>
              </div>
              <span className={styles["comparison__vs"]}>vs</span>
              <div className={styles["comparison__avatars"]}>
                <div
                  className={styles["comparison__avatar"]}
                  style={{ background: "#115e59", zIndex: 3 }}
                >
                  MIT
                </div>
                <div
                  className={styles["comparison__avatar"]}
                  style={{ background: "#991b1b", zIndex: 2 }}
                >
                  HBS
                </div>
                <div
                  className={styles["comparison__avatar"]}
                  style={{ background: "#1e40af", zIndex: 1 }}
                >
                  STN
                </div>
              </div>
            </div>
            <div className={styles["comparison__metrics"]}>
              <div className={styles["comparison__metric"]}>
                <span className={styles["comparison__metricLabel"]}>AVG SHARE OF VOICE</span>
                <div
                  className={`${styles["comparison__metricValue"]} ${styles["comparison__metricValuePrimary"]}`}
                >
                  28.4%
                </div>
              </div>
              <div className={styles["comparison__metric"]}>
                <span className={styles["comparison__metricLabel"]}>TOPIC VELOCITY</span>
                <div
                  className={`${styles["comparison__metricValue"]} ${styles["comparison__metricValueSecondary"]}`}
                >
                  +12.1%
                </div>
              </div>
              <div className={styles["comparison__metric"]}>
                <span className={styles["comparison__metricLabel"]}>GLOBAL RANK</span>
                <div
                  className={`${styles["comparison__metricValue"]} ${styles["comparison__metricValueNeutral"]}`}
                >
                  Top 5
                </div>
              </div>
              <div className={styles["comparison__metric"]}>
                <span className={styles["comparison__metricLabel"]}>CITATION DOMINANCE</span>
                <div
                  className={`${styles["comparison__metricValue"]} ${styles["comparison__metricValueContainer"]}`}
                >
                  Tier 1
                </div>
              </div>
            </div>
            <div className={styles["comparison__bars"]}>
              {DEMO_BARS.map((row) => (
                <div key={row.label} className={styles["comparison__barRow"]}>
                  <div className={styles["comparison__barMeta"]}>
                    <span>{row.label}</span>
                    <span>{row.pct}% Dominance</span>
                  </div>
                  <div className={styles["comparison__track"]}>
                    <div
                      className={`${styles["comparison__fill"]} ${
                        row.tone === "primary"
                          ? styles["comparison__fillPrimary"]
                          : row.tone === "secondary"
                            ? styles["comparison__fillSecondary"]
                            : styles["comparison__fillContainer"]
                      }`}
                      style={{ width: `${row.pct}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div
          id="live-leaderboards"
          ref={leaderboardsRef}
          className={styles["leaderboards"]}
        >
          {isLoading ? (
            <Skeleton paragraph={{ rows: 10 }} active />
          ) : (
            <>
              <Leaderboard
                title="Top Institutions by Publication"
                description="These institutions lead in total publication output across all research topics, reflecting their scale and research activity."
                data={topData.institutions}
                nameKey="name"
                valueKey="publication_count"
                variant="institution"
              />
              {/* <div className={styles["leaderboards__ctaRow"]}>
                <button
                  type="button"
                  className={styles["btnPrimary"]}
                  onClick={() => setIsModalOpen(true)}
                >
                  Start Benchmarking
                </button>
              </div> */}
              <Leaderboard
                title="Top Research Topics by Publication"
                description="The most published research topics across all institutions, showcasing where the academic community is focusing its efforts."
                data={topData.keywords}
                nameKey="keyword"
                valueKey="publication_count"
                variant="keyword"
              />
            </>
          )}
        </div>

        {/* <section className={styles["ctaFinal"]} aria-labelledby="cta-final-heading">
          <div className={styles["ctaFinal__inner"]}>
            <h2 id="cta-final-heading" className={styles["ctaFinal__title"]}>
              Secure Your Institutional Authority.
            </h2>
            <p className={styles["ctaFinal__text"]}>
              The landscape of academic influence is shifting. Access your live benchmarking stream
              and start identifying your research momentum today.
            </p>
            <button
              type="button"
              className={styles["ctaFinal__btn"]}
              onClick={() => setIsModalOpen(true)}
            >
              Get Started for Free
            </button>
          </div>
        </section> */}
      </div>

      <BenchmarkModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        allKeywords={allKeywords}
        allInstitutions={allInstitutions}
      />
    </>
  );
};

export default LandingBenchmark;
